import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  BookmarkIcon,
  Lock,
  Video,
  ClipboardList,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { JournalReference } from "@shared/content-types";
import {
  classifyContentDiff,
  buildReferencedSectionsMap,
  type ProgramDaySnapshot,
} from "@/lib/content-diff";
import type { ProgramDay } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Props {
  programId: string;
  programLifecycle: "live" | "draft";
  dayNumber: number;
  allDays: ProgramDay[];
  initialContentJson: string;
  value: string;
  onChange: (json: string) => void;
  onCreateNewVersion?: () => void;
  /** Called by content admins who want to save structural changes directly to the live program. */
  onSaveToLive?: (content: string) => void;
  /** Whether the current user has content-admin (or admin) privileges. */
  isContentAdmin?: boolean;
}

const SAFE_TOP_FIELDS: Array<{ key: string; label: string; multiline?: boolean }> = [
  { key: "title", label: "Title" },
  { key: "overview", label: "Overview", multiline: true },
  { key: "description", label: "Description", multiline: true },
  { key: "intro", label: "Intro", multiline: true },
  { key: "focus", label: "Focus" },
  { key: "focusNote", label: "Focus Note", multiline: true },
  { key: "exercise", label: "Exercise", multiline: true },
  { key: "powerWords", label: "Power Words" },
  { key: "video", label: "Video URL" },
  { key: "videoUrl", label: "Video URL" },
];

const SAFE_SECTION_FIELDS: Array<{ key: string; label: string; multiline?: boolean }> = [
  { key: "title", label: "Title" },
  { key: "category", label: "Category" },
  { key: "description", label: "Description", multiline: true },
  { key: "prompt", label: "Prompt", multiline: true },
  { key: "content", label: "Content", multiline: true },
  { key: "intro", label: "Intro", multiline: true },
  { key: "note", label: "Note", multiline: true },
];

const V2_FIELD_TYPES: Array<{ value: string; label: string }> = [
  { value: "short-text", label: "Short Text" },
  { value: "long-text", label: "Long Text" },
  { value: "single-choice", label: "Single Choice" },
  { value: "multi-select", label: "Multi Select" },
  { value: "select", label: "Select (Dropdown)" },
  { value: "scale-1-3", label: "Scale 1–3" },
  { value: "scale-1-5", label: "Scale 1–5" },
  { value: "scale-1-10", label: "Scale 1–10" },
  { value: "acknowledgment", label: "Acknowledgment" },
  { value: "exam-question", label: "Exam Question" },
];

const V2_BLOCK_KINDS: Array<{ value: string; label: string }> = [
  { value: "paragraph", label: "Paragraph" },
  { value: "heading", label: "Heading" },
  { value: "bullets", label: "Bullet List" },
  { value: "numbered", label: "Numbered List" },
  { value: "blockquote", label: "Blockquote" },
  { value: "table", label: "Table" },
  { value: "code", label: "Code Block" },
  { value: "vimeo", label: "Vimeo Embed" },
  // Deliberately no "image" entry here: an image block needs an uploaded
  // object behind its path, and this editor has no upload control. Image
  // blocks are created by AI Import (from pasted clipboard images) and can
  // only be reviewed/edited (alt, caption, reorder, remove) here — adding a
  // blank one would create a path admins can never fill in, which the
  // storage-layer image validation would then always reject on save.
];

const V2_SECTION_TYPES = new Set([
  "welcome", "intro", "video", "scored", "quote",
  "pre-exam", "exam", "post-exam", "permission", "exam-sub",
]);

function parseSafe(json: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

type SectionKey = "sections" | "exercises";

function detectSectionKey(content: Record<string, unknown> | null): SectionKey {
  if (!content) return "sections";
  const sections = Array.isArray(content.sections) ? (content.sections as unknown[]) : null;
  const exercises = Array.isArray(content.exercises) ? (content.exercises as unknown[]) : null;
  if (exercises && exercises.length > 0 && (!sections || sections.length === 0)) return "exercises";
  if (sections && sections.length > 0) return "sections";
  if (exercises) return "exercises";
  return "sections";
}

function readSections(content: Record<string, unknown> | null, key: SectionKey = "sections"): Record<string, unknown>[] {
  if (!content) return [];
  const arr = content[key];
  return Array.isArray(arr) ? (arr as Record<string, unknown>[]) : [];
}

function readBookmarks(section: Record<string, unknown>): JournalReference[] {
  const list = section.journalReferences;
  if (Array.isArray(list)) return list as JournalReference[];
  const single = section.journalReference;
  if (single && typeof single === "object") return [single as JournalReference];
  return [];
}

/** Returns true if `arr` looks like a V2Block[] (objects with a `kind` string). */
function isV2BlockArray(arr: unknown): arr is Array<Record<string, unknown>> {
  if (!Array.isArray(arr)) return false;
  if (arr.length === 0) return true; // treat empty body as V2 when type is V2
  return typeof (arr[0] as Record<string, unknown>)?.kind === "string";
}

/** Returns true if `arr` looks like a V2OrderedContent[] (objects with a `kind` string). */
function isV2OrderedContent(arr: unknown): arr is Array<Record<string, unknown>> {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  const kinds = new Set(["block", "question", "expandable", "vimeo", "permissionWidget"]);
  return kinds.has((arr[0] as Record<string, unknown>)?.kind as string);
}

export default function StructuredDayEditor({
  programId,
  programLifecycle,
  dayNumber,
  allDays,
  initialContentJson,
  value,
  onChange,
  onCreateNewVersion,
  onSaveToLive,
  isContentAdmin = false,
}: Props) {
  const isLive = programLifecycle === "live";
  const [saveToLiveDialogOpen, setSaveToLiveDialogOpen] = useState(false);
  const parsed = useMemo(() => parseSafe(value), [value]);
  const sectionKey = useMemo(() => detectSectionKey(parsed), [parsed]);
  const sections = useMemo(() => readSections(parsed, sectionKey), [parsed, sectionKey]);
  const sectionLabel = sectionKey === "exercises" ? "Exercises" : "Sections";
  const itemLabel = sectionKey === "exercises" ? "Exercise" : "Section";

  const isExamDay = useMemo(() => {
    if (!parsed) return false;
    if (parsed.dayShape === "exam" || parsed.moduleShape === "exam") return true;
    const secs = Array.isArray(parsed.sections) ? parsed.sections as Array<Record<string, unknown>> : [];
    return secs.some((s) => s.type === "exam" || s.type === "pre-exam" || s.type === "post-exam");
  }, [parsed]);

  const programSnapshot: ProgramDaySnapshot[] = useMemo(
    () =>
      allDays.map((d) => ({
        dayNumber: d.dayNumber,
        content: parseSafe(d.content || "{}") as unknown as ProgramDaySnapshot["content"],
      })),
    [allDays],
  );

  const referencedMap = useMemo(
    () => buildReferencedSectionsMap(programSnapshot),
    [programSnapshot],
  );

  const initialParsed = useMemo(() => parseSafe(initialContentJson), [initialContentJson]);

  const diffResult = useMemo(() => {
    if (!parsed) return null;
    return classifyContentDiff(initialParsed as unknown as ProgramDaySnapshot["content"], parsed as unknown as ProgramDaySnapshot["content"], {
      dayNumber,
      referencedSections: referencedMap,
    });
  }, [parsed, initialParsed, dayNumber, referencedMap]);

  const dirty = value !== initialContentJson;
  const isStructural = diffResult ? !diffResult.safe : false;
  const blockedOnLive = isLive && isStructural;

  const updateContent = (mutator: (draft: Record<string, unknown>) => void) => {
    const draft: Record<string, unknown> = parsed ? JSON.parse(JSON.stringify(parsed)) : {};
    mutator(draft);
    onChange(JSON.stringify(draft, null, 2));
  };

  const updateSection = (
    idx: number,
    mutator: (s: Record<string, unknown>) => void,
  ) => {
    updateContent((draft) => {
      const arr = (draft[sectionKey] as Record<string, unknown>[] | undefined) ?? [];
      const next = [...arr];
      const copy = { ...(next[idx] || {}) };
      mutator(copy);
      next[idx] = copy;
      draft[sectionKey] = next;
    });
  };

  const addSection = () => {
    updateContent((draft) => {
      const arr = (draft[sectionKey] as Record<string, unknown>[] | undefined) ?? [];
      const seed = sectionKey === "exercises"
        ? { title: "New Exercise", points: 0, category: "", questions: [] }
        : { title: "New Section", content: "" };
      draft[sectionKey] = [...arr, seed];
    });
  };

  const removeSection = (idx: number) => {
    updateContent((draft) => {
      const arr = (draft[sectionKey] as Record<string, unknown>[] | undefined) ?? [];
      draft[sectionKey] = arr.filter((_, i) => i !== idx);
    });
  };

  const moveSection = (idx: number, dir: -1 | 1) => {
    updateContent((draft) => {
      const arr = (draft[sectionKey] as Record<string, unknown>[] | undefined) ?? [];
      const next = [...arr];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return;
      [next[idx], next[target]] = [next[target], next[idx]];
      draft[sectionKey] = next;
    });
  };

  if (!parsed) {
    return (
      <Card className="border-destructive/40">
        <CardContent className="py-6 text-sm text-destructive">
          Cannot parse content as JSON. Restore from a previous published version, or contact support to repair this day.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Diff classification banner */}
      {dirty && diffResult && (
        <div
          className={`rounded-lg border p-3 flex items-start gap-3 ${
            isStructural
              ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30"
              : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30"
          }`}
          data-testid="banner-diff-classification"
        >
          {isStructural ? (
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          ) : (
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          )}
          <div className="text-sm flex-1">
            <p className="font-medium">
              {isStructural ? "Structural change detected" : "Safe edit"}
              {blockedOnLive && " — requires a new version"}
            </p>
            {isStructural && (
              <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground space-y-0.5">
                {diffResult.reasons.slice(0, 5).map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
                {diffResult.reasons.length > 5 && (
                  <li>+{diffResult.reasons.length - 5} more…</li>
                )}
              </ul>
            )}
            {blockedOnLive && (
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  This program is <strong>live</strong>.{" "}
                  {isContentAdmin
                    ? "You can create a new version, or save directly to live (immediately affects enrolled members)."
                    : "To apply these structural changes, create a new version."}
                </p>
                <div className="flex gap-2 shrink-0">
                  {isContentAdmin && onSaveToLive && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setSaveToLiveDialogOpen(true)}
                      data-testid="button-banner-save-to-live"
                    >
                      Save to live
                    </Button>
                  )}
                  {onCreateNewVersion && (
                    <Button
                      size="sm"
                      onClick={onCreateNewVersion}
                      data-testid="button-banner-create-new-version"
                    >
                      Create new version
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top-level safe fields */}
      <Card>
        <CardContent className="space-y-4 py-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Top-Level Fields
          </h3>
          {SAFE_TOP_FIELDS.filter((f) => Object.prototype.hasOwnProperty.call(parsed, f.key) || f.key === "title" || f.key === "overview").map(
            (field) => {
              const v = parsed[field.key];
              if (typeof v !== "string" && v !== undefined) return null;
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label className="text-xs">{field.label}</Label>
                  {field.multiline ? (
                    <Textarea
                      value={(v as string) ?? ""}
                      onChange={(e) =>
                        updateContent((d) => {
                          d[field.key] = e.target.value;
                        })
                      }
                      rows={3}
                      data-testid={`input-top-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={(v as string) ?? ""}
                      onChange={(e) =>
                        updateContent((d) => {
                          d[field.key] = e.target.value;
                        })
                      }
                      data-testid={`input-top-${field.key}`}
                    />
                  )}
                </div>
              );
            },
          )}

          {/* Objectives bullets */}
          {Array.isArray(parsed.objectives) && (
            <BulletList
              label="Objectives"
              items={parsed.objectives as string[]}
              onChange={(next) =>
                updateContent((d) => {
                  d.objectives = next;
                })
              }
              isLive={isLive}
            />
          )}
        </CardContent>
      </Card>

      {/* Video URL */}
      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Video
            </h3>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="input-video-url">
              Vimeo Embed URL
            </Label>
            <Input
              id="input-video-url"
              data-testid="input-video-url"
              placeholder="https://player.vimeo.com/video/123456789"
              value={
                (
                  parsed.videoSection as Record<string, unknown> | undefined
                )?.videoUrl as string ?? ""
              }
              onChange={(e) =>
                updateContent((d) => {
                  const existing =
                    typeof d.videoSection === "object" && d.videoSection !== null
                      ? (d.videoSection as Record<string, unknown>)
                      : {};
                  d.videoSection = { ...existing, videoUrl: e.target.value };
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              Paste the Vimeo player embed URL (e.g.{" "}
              <span className="font-mono">https://player.vimeo.com/video/…</span>).
              Leave blank to show the "Video coming soon" placeholder.
            </p>
            {(() => {
              const vs = parsed.videoSection as Record<string, unknown> | undefined;
              const url = vs?.videoUrl as string | undefined;
              if (!url) return null;
              const isVimeoEmbed = /^https:\/\/player\.vimeo\.com\/video\/\d+/.test(url);
              if (isVimeoEmbed) return null;
              return (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠ URL doesn't look like a Vimeo embed URL. The inline player may not work. Expected format:{" "}
                  <span className="font-mono">https://player.vimeo.com/video/&lt;id&gt;</span>
                </p>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Sample Response — shown for simple days (no sections/exercises) */}
      {sections.length === 0 && (
        <Card>
          <CardContent className="space-y-3 py-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Sample Response
            </h3>
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="input-sample-response">
                Sample Response
              </Label>
              <Textarea
                id="input-sample-response"
                data-testid="input-sample-response"
                placeholder="Provide an example of a strong response to this exercise…"
                value={(parsed.sampleResponse as string) ?? ""}
                rows={5}
                onChange={(e) =>
                  updateContent((d) => {
                    if (e.target.value) {
                      d.sampleResponse = e.target.value;
                    } else {
                      delete d.sampleResponse;
                    }
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Optional. When filled in, participants see a &ldquo;View Sample Response&rdquo; button after submitting their answer. Clear this field to hide the button.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {sectionLabel} ({sections.length})
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={addSection}
            data-testid="button-add-section"
          >
            <Plus className="w-4 h-4 mr-1" /> Add {itemLabel}
          </Button>
        </div>

        {sections.map((section, idx) => (
          <SectionEditor
            key={idx}
            section={section}
            idx={idx}
            sectionsCount={sections.length}
            isLive={isLive}
            currentDayNumber={dayNumber}
            allDays={allDays}
            onUpdate={(mutator) => updateSection(idx, mutator)}
            onRemove={() => removeSection(idx)}
            onMove={(dir) => moveSection(idx, dir)}
          />
        ))}

        {sections.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-6 text-sm text-muted-foreground text-center">
              No {sectionLabel.toLowerCase()} yet. Click <em>Add {itemLabel}</em> to create one.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Exam Questions panel — shown only for exam-type days */}
      {isExamDay && (
        <ExamQuestionsPanel programId={programId} />
      )}

      {/* Save-to-live confirmation dialog — content admins only */}
      <AlertDialog open={saveToLiveDialogOpen} onOpenChange={setSaveToLiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save structural changes to live program?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will immediately apply structural changes to the live program.
                  All currently enrolled members will be affected right away.
                </p>
                {diffResult && !diffResult.safe && (
                  <div>
                    <p className="font-medium text-sm mb-1">Changes detected:</p>
                    <ul className="list-disc pl-4 text-sm space-y-0.5">
                      {diffResult.reasons.slice(0, 8).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                      {diffResult.reasons.length > 8 && (
                        <li>+{diffResult.reasons.length - 8} more…</li>
                      )}
                    </ul>
                  </div>
                )}
                <p className="text-amber-700 dark:text-amber-400 text-sm font-medium">
                  ⚠ No automatic migration is performed for members with in-progress answers.
                  This action is recorded in the audit log.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setSaveToLiveDialogOpen(false);
                onSaveToLive?.(value);
              }}
              data-testid="button-confirm-save-to-live"
            >
              Save to live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Exam Questions Panel ────────────────────────────────────────────────────

interface ExamQuestion {
  id: string;
  programId: string;
  category: string;
  question: string;
  options: string[];
  correctAnswer: number;
  points: number;
  sortOrder: number;
}

interface QuestionFormState {
  category: string;
  question: string;
  options: string[];
  correctAnswer: number;
  points: number;
}

const EMPTY_QUESTION_FORM: QuestionFormState = {
  category: "",
  question: "",
  options: ["", "", "", ""],
  correctAnswer: 0,
  points: 1,
};

function ExamQuestionsPanel({ programId }: { programId: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const queryKey = [`/api/admin/programs/${programId}/exam-questions`];

  const { data: questions = [], isLoading } = useQuery<ExamQuestion[]>({
    queryKey,
  });

  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<QuestionFormState>(EMPTY_QUESTION_FORM);

  const openNew = () => {
    setForm(EMPTY_QUESTION_FORM);
    setEditingId("new");
  };

  const openEdit = (q: ExamQuestion) => {
    setForm({
      category: q.category,
      question: q.question,
      options: [...q.options],
      correctAnswer: q.correctAnswer,
      points: q.points,
    });
    setEditingId(q.id);
  };

  const closeForm = () => setEditingId(null);

  const createMutation = useMutation({
    mutationFn: async (body: QuestionFormState) => {
      const res = await apiRequest("POST", `/api/admin/programs/${programId}/exam-questions`, body);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      closeForm();
      toast({ title: "Question added" });
    },
    onError: () => toast({ title: "Failed to save question", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: QuestionFormState }) => {
      const res = await apiRequest("PUT", `/api/admin/programs/${programId}/exam-questions/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      closeForm();
      toast({ title: "Question updated" });
    },
    onError: () => toast({ title: "Failed to update question", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/programs/${programId}/exam-questions/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast({ title: "Question deleted" });
    },
    onError: () => toast({ title: "Failed to delete question", variant: "destructive" }),
  });

  const handleSave = () => {
    if (!form.category.trim() || !form.question.trim()) return;
    if (form.options.some((o) => !o.trim())) return;
    if (editingId === "new") {
      createMutation.mutate(form);
    } else if (editingId) {
      updateMutation.mutate({ id: editingId, body: form });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const sorted = useMemo(
    () => [...questions].sort((a, b) => a.sortOrder - b.sortOrder),
    [questions],
  );

  const reorderMutation = useMutation({
    mutationFn: async ({ a, b }: { a: ExamQuestion; b: ExamQuestion }) => {
      await Promise.all([
        apiRequest("PUT", `/api/admin/programs/${programId}/exam-questions/${a.id}`, {
          category: a.category,
          question: a.question,
          options: a.options,
          correctAnswer: a.correctAnswer,
          points: a.points,
          sortOrder: b.sortOrder,
        }),
        apiRequest("PUT", `/api/admin/programs/${programId}/exam-questions/${b.id}`, {
          category: b.category,
          question: b.question,
          options: b.options,
          correctAnswer: b.correctAnswer,
          points: b.points,
          sortOrder: a.sortOrder,
        }),
      ]);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
    onError: () => toast({ title: "Failed to reorder question", variant: "destructive" }),
  });

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const target = sorted[idx + dir];
    if (!target) return;
    reorderMutation.mutate({ a: sorted[idx], b: target });
  };

  return (
    <div className="space-y-3" data-testid="exam-questions-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <ClipboardList className="w-4 h-4" />
          Exam Questions ({questions.length})
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={openNew}
          disabled={editingId !== null}
          data-testid="button-add-exam-question"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Question
        </Button>
      </div>

      {/* Inline form for new / editing */}
      {editingId !== null && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="pt-4 space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {editingId === "new" ? "New Question" : "Edit Question"}
            </h4>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. Mindset"
                  data-testid="input-exam-question-category"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Points</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.points}
                  onChange={(e) => setForm((f) => ({ ...f, points: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                  data-testid="input-exam-question-points"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Question Prompt</Label>
              <Textarea
                value={form.question}
                onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                rows={2}
                placeholder="Enter the question text…"
                data-testid="input-exam-question-prompt"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Options (select correct answer with radio)</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => setForm((f) => ({ ...f, options: [...f.options, ""] }))}
                  data-testid="button-add-option"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>
              {form.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`exam-correct-${editingId}`}
                    checked={form.correctAnswer === oi}
                    onChange={() => setForm((f) => ({ ...f, correctAnswer: oi }))}
                    className="accent-primary"
                    data-testid={`radio-correct-answer-${oi}`}
                  />
                  <Input
                    value={opt}
                    onChange={(e) => {
                      const next = [...form.options];
                      next[oi] = e.target.value;
                      setForm((f) => ({ ...f, options: next }));
                    }}
                    placeholder={`Option ${oi + 1}…`}
                    className="flex-1 h-8 text-sm"
                    data-testid={`input-option-${oi}`}
                  />
                  {form.options.length > 2 && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 shrink-0"
                      onClick={() => {
                        const next = form.options.filter((_, j) => j !== oi);
                        const newCorrect = form.correctAnswer >= next.length ? next.length - 1 : form.correctAnswer;
                        setForm((f) => ({ ...f, options: next, correctAnswer: newCorrect }));
                      }}
                      data-testid={`button-remove-option-${oi}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
              {form.correctAnswer >= form.options.length && (
                <p className="text-xs text-destructive">Select a correct answer above.</p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving || !form.category.trim() || !form.question.trim() || form.options.some((o) => !o.trim())}
                data-testid="button-save-exam-question"
              >
                {isSaving ? "Saving…" : "Save Question"}
              </Button>
              <Button size="sm" variant="ghost" onClick={closeForm} data-testid="button-cancel-exam-question">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question list */}
      {isLoading ? (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">Loading questions…</CardContent>
        </Card>
      ) : questions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground text-center">
            No exam questions yet. Import the Day 46 Markdown fixture or click <em>Add Question</em>.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-3 space-y-2">
            {sorted.map((q, qi) => (
              <div
                key={q.id}
                className="rounded-md border bg-muted/20 p-2.5 space-y-1"
                data-testid={`exam-question-row-${q.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5"
                      onClick={() => moveQuestion(qi, -1)}
                      disabled={qi === 0 || reorderMutation.isPending || editingId !== null}
                      aria-label="Move question up"
                      data-testid={`button-move-up-exam-question-${q.id}`}
                    >
                      <ChevronUp className="w-3 h-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5"
                      onClick={() => moveQuestion(qi, 1)}
                      disabled={qi === sorted.length - 1 || reorderMutation.isPending || editingId !== null}
                      aria-label="Move question down"
                      data-testid={`button-move-down-exam-question-${q.id}`}
                    >
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground font-mono">#{qi + 1}</span>
                      <Badge variant="secondary" className="text-xs">{q.category}</Badge>
                      <Badge variant="outline" className="text-xs">{q.points} pt{q.points !== 1 ? "s" : ""}</Badge>
                    </div>
                    <p className="text-sm font-medium">{q.question}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => openEdit(q)}
                      disabled={editingId !== null}
                      data-testid={`button-edit-exam-question-${q.id}`}
                    >
                      <span className="sr-only">Edit</span>
                      ✏️
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => deleteMutation.mutate(q.id)}
                      disabled={deleteMutation.isPending}
                      data-testid={`button-delete-exam-question-${q.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <ul className="space-y-0.5 pl-8">
                  {q.options.map((opt, oi) => (
                    <li
                      key={oi}
                      className={`text-xs flex items-center gap-1.5 ${oi === q.correctAnswer ? "text-emerald-700 dark:text-emerald-400 font-medium" : "text-muted-foreground"}`}
                      data-testid={`option-${q.id}-${oi}`}
                    >
                      {oi === q.correctAnswer ? (
                        <CheckCircle className="w-3 h-3 shrink-0" />
                      ) : (
                        <span className="w-3 h-3 rounded-full border border-muted-foreground/40 inline-block shrink-0" />
                      )}
                      {opt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── V2 Block Editors ───────────────────────────────────────────────────────

function V2BlockEditor({
  block,
  blockIdx,
  sectionIdx,
  isLive,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
}: {
  block: Record<string, unknown>;
  blockIdx: number;
  sectionIdx: number;
  isLive: boolean;
  onChange: (next: Record<string, unknown>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const kind = block.kind as string;
  const testBase = `block-${sectionIdx}-${blockIdx}`;

  return (
    <div className="rounded-md border bg-muted/20 p-2.5 space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-[10px] shrink-0">
          {V2_BLOCK_KINDS.find((k) => k.value === kind)?.label ?? kind}
        </Badge>
        <div className="flex-1" />
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          disabled={isFirst}
          onClick={() => onMove(-1)}
          data-testid={`button-move-up-${testBase}`}
        >
          <ChevronUp className="w-3 h-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          disabled={isLast}
          onClick={() => onMove(1)}
          data-testid={`button-move-down-${testBase}`}
        >
          <ChevronDown className="w-3 h-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          disabled={isLive}
          title={isLive ? "Removing blocks requires a new version" : "Remove block"}
          onClick={onRemove}
          data-testid={`button-remove-${testBase}`}
        >
          {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
        </Button>
      </div>

      {(kind === "paragraph" || kind === "blockquote" || kind === "code") && (
        <Textarea
          value={(block.text as string) ?? ""}
          rows={kind === "paragraph" ? 3 : 4}
          placeholder={kind === "code" ? "Code content…" : kind === "blockquote" ? "Blockquote text…" : "Paragraph text…"}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          data-testid={`input-${testBase}-text`}
          className="text-sm"
        />
      )}

      {kind === "heading" && (
        <div className="flex gap-2">
          <Select
            value={String(block.level ?? 3)}
            onValueChange={(v) => onChange({ ...block, level: Number(v) })}
          >
            <SelectTrigger className="w-24 h-8 text-xs" data-testid={`select-${testBase}-level`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">H3</SelectItem>
              <SelectItem value="4">H4</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={(block.text as string) ?? ""}
            placeholder="Heading text…"
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            data-testid={`input-${testBase}-text`}
            className="text-sm h-8"
          />
        </div>
      )}

      {(kind === "bullets" || kind === "numbered") && (
        <V2ItemListEditor
          items={(block.items as string[]) ?? []}
          onChange={(next) => onChange({ ...block, items: next })}
          isLive={isLive}
          testBase={testBase}
          placeholder={kind === "bullets" ? "Bullet item…" : "Numbered item…"}
        />
      )}

      {kind === "table" && (
        <V2TableEditor
          rows={(block.rows as string[][]) ?? []}
          onChange={(next) => onChange({ ...block, rows: next })}
          isLive={isLive}
          testBase={testBase}
        />
      )}

      {kind === "vimeo" && (
        <Input
          value={(block.embedUrl as string) ?? ""}
          placeholder="https://player.vimeo.com/video/…"
          onChange={(e) => onChange({ ...block, embedUrl: e.target.value })}
          data-testid={`input-${testBase}-embedUrl`}
          className="text-sm"
        />
      )}

      {kind === "image" && (
        <div className="space-y-2">
          {block.path ? (
            <img
              src={block.path as string}
              alt={(block.alt as string) ?? ""}
              className="max-h-40 rounded border border-border object-contain"
              data-testid={`img-${testBase}-preview`}
            />
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No image path set — this image was not pasted through AI Import and has no uploaded file.
            </p>
          )}
          <Input
            value={(block.path as string) ?? ""}
            readOnly
            placeholder="/objects/uploads/…"
            data-testid={`input-${testBase}-path`}
            className="text-xs font-mono text-muted-foreground"
          />
          <Input
            value={(block.alt as string) ?? ""}
            placeholder="Alt text (required, describes the image for screen readers)"
            onChange={(e) => onChange({ ...block, alt: e.target.value })}
            data-testid={`input-${testBase}-alt`}
            className="text-sm"
          />
          <Input
            value={(block.caption as string) ?? ""}
            placeholder="Caption (optional)"
            onChange={(e) => onChange({ ...block, caption: e.target.value || undefined })}
            data-testid={`input-${testBase}-caption`}
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
}

// ─── Exam Question Options Editor ───────────────────────────────────────────
// Shows options list with radio buttons to mark the correct answer.

export function ExamQuestionOptionsEditor({
  options,
  correctAnswerIndex,
  onChange,
  testBase,
}: {
  options: string[];
  correctAnswerIndex: number | undefined;
  onChange: (next: { options: string[]; correctAnswerIndex: number | undefined }) => void;
  testBase: string;
}) {
  const updateOption = (i: number, val: string) => {
    const next = [...options];
    next[i] = val;
    onChange({ options: next, correctAnswerIndex });
  };

  const removeOption = (i: number) => {
    const next = options.filter((_, j) => j !== i);
    if (correctAnswerIndex === undefined) {
      onChange({ options: next, correctAnswerIndex: undefined });
    } else {
      const newCorrect =
        correctAnswerIndex >= i && correctAnswerIndex > 0
          ? correctAnswerIndex - 1
          : correctAnswerIndex >= next.length
          ? 0
          : correctAnswerIndex;
      onChange({ options: next, correctAnswerIndex: Math.min(newCorrect, Math.max(0, next.length - 1)) });
    }
  };

  const addOption = () => {
    onChange({ options: [...options, ""], correctAnswerIndex });
  };

  const setCorrect = (i: number) => {
    onChange({ options, correctAnswerIndex: i });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Options</span>
        <span className="text-[10px] text-muted-foreground">(click the circle to mark the correct answer)</span>
      </div>
      {options.map((item, i) => {
        const isCorrect = correctAnswerIndex !== undefined && i === correctAnswerIndex;
        return (
          <div
            key={i}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors ${
              isCorrect
                ? "bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-700"
                : "bg-muted/30 border border-transparent"
            }`}
            data-testid={`exam-option-row-${testBase}-${i}`}
          >
            <button
              type="button"
              onClick={() => setCorrect(i)}
              title={isCorrect ? "Correct answer" : "Mark as correct"}
              className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                isCorrect
                  ? "border-emerald-600 bg-emerald-600"
                  : "border-muted-foreground/40 hover:border-emerald-500"
              }`}
              data-testid={`radio-correct-${testBase}-${i}`}
            >
              {isCorrect && <span className="block w-1.5 h-1.5 rounded-full bg-white" />}
            </button>
            <Input
              value={item}
              placeholder={`Option ${i + 1}…`}
              onChange={(e) => updateOption(i, e.target.value)}
              className={`text-sm h-7 flex-1 ${isCorrect ? "font-medium" : ""}`}
              data-testid={`input-${testBase}-option-${i}`}
            />
            {isCorrect && (
              <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 shrink-0 pr-1">
                Correct
              </span>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={() => removeOption(i)}
              data-testid={`button-remove-${testBase}-option-${i}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        );
      })}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-xs"
        onClick={addOption}
        data-testid={`button-add-${testBase}-option`}
      >
        <Plus className="w-3 h-3 mr-1" /> Add option
      </Button>
      {options.length > 0 && correctAnswerIndex === undefined && (
        <p
          className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-1"
          data-testid={`warn-no-correct-answer-${testBase}`}
        >
          <span>⚠</span>
          No correct answer selected — grading will default to the first option.
        </p>
      )}
    </div>
  );
}

function V2ItemListEditor({
  items,
  onChange,
  isLive,
  testBase,
  placeholder,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  isLive: boolean;
  testBase: string;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex gap-1.5">
          <Input
            value={item}
            placeholder={placeholder ?? "Item…"}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            className="text-sm h-7"
            data-testid={`input-${testBase}-item-${i}`}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            disabled={isLive}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            data-testid={`button-remove-${testBase}-item-${i}`}
          >
            {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
          </Button>
        </div>
      ))}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-xs"
        onClick={() => onChange([...items, ""])}
        data-testid={`button-add-${testBase}-item`}
      >
        <Plus className="w-3 h-3 mr-1" /> Add item
      </Button>
    </div>
  );
}

function V2TableEditor({
  rows,
  onChange,
  isLive,
  testBase,
}: {
  rows: string[][];
  onChange: (next: string[][]) => void;
  isLive: boolean;
  testBase: string;
}) {
  const colCount = rows[0]?.length ?? 2;

  const updateCell = (ri: number, ci: number, val: string) => {
    const next = rows.map((r) => [...r]);
    next[ri][ci] = val;
    onChange(next);
  };

  const addRow = () => onChange([...rows, Array(colCount).fill("")]);
  const removeRow = (ri: number) => onChange(rows.filter((_, i) => i !== ri));

  return (
    <div className="space-y-1.5 overflow-x-auto">
      {rows.map((row, ri) => (
        <div key={ri} className="flex gap-1.5 items-center">
          {row.map((cell, ci) => (
            <Input
              key={ci}
              value={cell}
              placeholder={ri === 0 ? `Header ${ci + 1}` : `Cell ${ri},${ci + 1}`}
              onChange={(e) => updateCell(ri, ci, e.target.value)}
              className={`text-xs h-7 ${ri === 0 ? "font-medium" : ""}`}
              data-testid={`input-${testBase}-row-${ri}-col-${ci}`}
            />
          ))}
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            disabled={isLive || rows.length <= 1}
            onClick={() => removeRow(ri)}
            data-testid={`button-remove-${testBase}-row-${ri}`}
          >
            {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
          </Button>
        </div>
      ))}
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addRow}
        data-testid={`button-add-${testBase}-row`}>
        <Plus className="w-3 h-3 mr-1" /> Add row
      </Button>
    </div>
  );
}

function V2BlockListEditor({
  label,
  blocks,
  sectionIdx,
  fieldKey,
  isLive,
  onChange,
}: {
  label: string;
  blocks: Array<Record<string, unknown>>;
  sectionIdx: number;
  fieldKey: string;
  isLive: boolean;
  onChange: (next: Array<Record<string, unknown>>) => void;
}) {
  const testBase = `${fieldKey}-${sectionIdx}`;

  const updateBlock = (i: number, next: Record<string, unknown>) => {
    const arr = [...blocks];
    arr[i] = next;
    onChange(arr);
  };

  const removeBlock = (i: number) => onChange(blocks.filter((_, j) => j !== i));

  const moveBlock = (i: number, dir: -1 | 1) => {
    const arr = [...blocks];
    const target = i + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[i], arr[target]] = [arr[target], arr[i]];
    onChange(arr);
  };

  const addBlock = (kind: string) => {
    const seed: Record<string, unknown> =
      kind === "paragraph" ? { kind, text: "" }
      : kind === "heading" ? { kind, level: 3, text: "" }
      : kind === "bullets" || kind === "numbered" ? { kind, items: [""] }
      : kind === "blockquote" ? { kind, text: "" }
      : kind === "table" ? { kind, rows: [["Header 1", "Header 2"], ["", ""]] }
      : kind === "code" ? { kind, language: null, text: "" }
      : kind === "vimeo" ? { kind, embedUrl: "" }
      : kind === "image" ? { kind, path: "", alt: "" }
      : { kind };
    onChange([...blocks, seed]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label} ({blocks.length} blocks)</Label>
        <Select onValueChange={addBlock}>
          <SelectTrigger className="w-36 h-7 text-xs" data-testid={`select-add-${testBase}`}>
            <Plus className="w-3 h-3 mr-1" />
            <SelectValue placeholder="Add block" />
          </SelectTrigger>
          <SelectContent>
            {V2_BLOCK_KINDS.map((k) => (
              <SelectItem key={k.value} value={k.value} className="text-xs">{k.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {blocks.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No blocks yet. Add one above.</p>
      )}
      {blocks.map((block, i) => (
        <V2BlockEditor
          key={i}
          block={block}
          blockIdx={i}
          sectionIdx={sectionIdx}
          isLive={isLive}
          onChange={(next) => updateBlock(i, next)}
          onRemove={() => removeBlock(i)}
          onMove={(dir) => moveBlock(i, dir)}
          isFirst={i === 0}
          isLast={i === blocks.length - 1}
        />
      ))}
    </div>
  );
}

// ─── V2 Ordered Content Editor ──────────────────────────────────────────────
// Edits a V2OrderedContent[] array (kind: block | question | expandable | vimeo)

function V2ContentEditor({
  content,
  sectionIdx,
  isLive,
  onChange,
  inScoredSection,
}: {
  content: Array<Record<string, unknown>>;
  sectionIdx: number;
  isLive: boolean;
  onChange: (next: Array<Record<string, unknown>>) => void;
  inScoredSection?: boolean;
}) {
  const updateItem = (i: number, next: Record<string, unknown>) => {
    const arr = [...content];
    arr[i] = next;
    onChange(arr);
  };

  const removeItem = (i: number) => onChange(content.filter((_, j) => j !== i));

  const moveItem = (i: number, dir: -1 | 1) => {
    const arr = [...content];
    const target = i + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[i], arr[target]] = [arr[target], arr[i]];
    onChange(arr);
  };

  const addBlock = (kind: string) => {
    const blockSeed: Record<string, unknown> =
      kind === "paragraph" ? { kind: "paragraph", text: "" }
      : kind === "heading" ? { kind: "heading", level: 3, text: "" }
      : kind === "bullets" || kind === "numbered" ? { kind, items: [""] }
      : kind === "blockquote" ? { kind: "blockquote", text: "" }
      : kind === "table" ? { kind: "table", rows: [["Header 1", "Header 2"], ["", ""]] }
      : kind === "code" ? { kind: "code", language: null, text: "" }
      : kind === "vimeo" ? { kind: "vimeo", embedUrl: "" }
      : kind === "image" ? { kind: "image", path: "", alt: "" }
      : { kind };

    const qIndices = content.filter((c) => c.kind === "question").map((c) => (c.questionIndex as number) ?? 0);
    const nextQ = qIndices.length === 0 ? 0 : Math.max(...qIndices) + 1;
    const eIndices = content.filter((c) => c.kind === "expandable").map((c) => (c.expandableIndex as number) ?? 0);
    const nextE = eIndices.length === 0 ? 0 : Math.max(...eIndices) + 1;

    if (kind === "question") {
      onChange([...content, {
        kind: "question",
        questionIndex: nextQ,
        question: { type: "short-text", required: true, prompt: "" },
      }]);
    } else if (kind === "expandable") {
      onChange([...content, {
        kind: "expandable",
        expandableIndex: nextE,
        expandable: { kind: "regular", title: "New Expandable", body: [] },
      }]);
    } else {
      onChange([...content, { kind: "block", block: blockSeed }]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Ordered Content ({content.length} items)</Label>
        <Select onValueChange={addBlock}>
          <SelectTrigger className="w-40 h-7 text-xs" data-testid={`select-add-content-${sectionIdx}`}>
            <Plus className="w-3 h-3 mr-1" />
            <SelectValue placeholder="Add item" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paragraph" className="text-xs">Paragraph</SelectItem>
            <SelectItem value="heading" className="text-xs">Heading</SelectItem>
            <SelectItem value="bullets" className="text-xs">Bullet List</SelectItem>
            <SelectItem value="numbered" className="text-xs">Numbered List</SelectItem>
            <SelectItem value="blockquote" className="text-xs">Blockquote</SelectItem>
            <SelectItem value="table" className="text-xs">Table</SelectItem>
            <SelectItem value="code" className="text-xs">Code Block</SelectItem>
            <SelectItem value="vimeo" className="text-xs">Vimeo Embed</SelectItem>
            {/* No "Image" entry: an image block needs an uploaded object behind
                its path, and this editor has no upload control. Image blocks
                come from AI Import (pasted clipboard images); they can be
                reviewed/edited/reordered/removed here once present. */}
            <SelectItem value="question" className="text-xs">Question</SelectItem>
            <SelectItem value="expandable" className="text-xs">Expandable Panel</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {content.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No content items yet. Add one above.</p>
      )}

      {content.map((item, i) => (
        <div key={i} className="rounded-md border bg-muted/20 p-2.5 space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] shrink-0 capitalize">
              {item.kind as string}
            </Badge>
            <div className="flex-1" />
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0}
              onClick={() => moveItem(i, -1)} data-testid={`button-move-up-content-${sectionIdx}-${i}`}>
              <ChevronUp className="w-3 h-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === content.length - 1}
              onClick={() => moveItem(i, 1)} data-testid={`button-move-down-content-${sectionIdx}-${i}`}>
              <ChevronDown className="w-3 h-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={isLive}
              title={isLive ? "Removing items requires a new version" : "Remove"}
              onClick={() => removeItem(i)} data-testid={`button-remove-content-${sectionIdx}-${i}`}>
              {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
            </Button>
          </div>

          {item.kind === "block" && (
            <V2BlockEditor
              block={(item.block as Record<string, unknown>) ?? {}}
              blockIdx={i}
              sectionIdx={sectionIdx}
              isLive={isLive}
              onChange={(next) => updateItem(i, { ...item, block: next })}
              onRemove={() => removeItem(i)}
              onMove={(dir) => moveItem(i, dir)}
              isFirst={i === 0}
              isLast={i === content.length - 1}
            />
          )}

          {item.kind === "vimeo" && (
            <Input
              value={(item.embedUrl as string) ?? ""}
              placeholder="https://player.vimeo.com/video/…"
              onChange={(e) => updateItem(i, { ...item, embedUrl: e.target.value })}
              data-testid={`input-content-${sectionIdx}-${i}-embedUrl`}
              className="text-sm"
            />
          )}

          {item.kind === "question" && (
            <V2QuestionEditor
              question={(item.question as Record<string, unknown>) ?? {}}
              isLive={isLive}
              testBase={`content-${sectionIdx}-${i}`}
              onChange={(next) => updateItem(i, { ...item, question: next })}
              inScoredSection={inScoredSection}
            />
          )}

          {item.kind === "expandable" && (
            <V2ExpandableEditor
              expandable={(item.expandable as Record<string, unknown>) ?? {}}
              isLive={isLive}
              testBase={`content-${sectionIdx}-${i}`}
              onChange={(next) => updateItem(i, { ...item, expandable: next })}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── V2 Question Editor ─────────────────────────────────────────────────────

function V2QuestionEditor({
  question,
  isLive,
  testBase,
  onChange,
  inScoredSection,
}: {
  question: Record<string, unknown>;
  isLive: boolean;
  testBase: string;
  onChange: (next: Record<string, unknown>) => void;
  /** When true (scored/quiz section), single-choice and multi-select questions
   * show the correct-answer radio selector alongside their options. */
  inScoredSection?: boolean;
}) {
  const type = (question.type as string) ?? "short-text";
  const isChoiceType = type === "single-choice" || type === "multi-select";
  const hasOptions = isChoiceType || type === "select";
  const isExamQuestion = type === "exam-question";
  const isAck = type === "acknowledgment";
  // In a scored section, single-choice questions use ExamQuestionOptionsEditor
  // so the admin can mark the correct answer.
  // multi-select is excluded: responses are stored as JSON arrays which are
  // incompatible with the single-index answer-key model — expose the UI only
  // once a multi-answer schema lands.
  const showAnswerKeyEditor = (isExamQuestion || (inScoredSection && type === "single-choice"));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Prompt</Label>
          <Textarea
            value={(question.prompt as string) ?? ""}
            rows={2}
            placeholder="Question prompt…"
            onChange={(e) => onChange({ ...question, prompt: e.target.value })}
            data-testid={`input-${testBase}-prompt`}
            className="text-sm"
          />
        </div>
      </div>

      <div className="flex gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Type</Label>
          <Select
            value={type}
            onValueChange={(v) => {
              const next: Record<string, unknown> = { ...question, type: v };
              if (!["single-choice", "multi-select", "select", "exam-question"].includes(v)) {
                delete next.options;
              }
              if (v === "exam-question" && !Array.isArray(next.options)) {
                next.options = [];
                delete next.correctAnswerIndex;
              }
              // In a scored section, initialize correctAnswerIndex for single-choice
              // (multi-select excluded — incompatible JSON-array storage format)
              if (inScoredSection && v === "single-choice") {
                if (next.correctAnswerIndex === undefined) next.correctAnswerIndex = 0;
                if (!Array.isArray(next.options)) next.options = [];
              }
              // Only clear correctAnswerIndex when leaving all answer-key-eligible types
              const keepCorrectAnswer =
                v === "exam-question" ||
                (inScoredSection && v === "single-choice");
              if (!keepCorrectAnswer) delete next.correctAnswerIndex;
              if (v !== "acknowledgment") delete next.acknowledgmentLabel;
              onChange(next);
            }}
            disabled={isLive}
          >
            <SelectTrigger className="w-44 h-8 text-xs" data-testid={`select-${testBase}-type`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {V2_FIELD_TYPES.map((ft) => (
                <SelectItem key={ft.value} value={ft.value} className="text-xs">{ft.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLive && (
            <p className="text-[10px] text-amber-600">Type changes require a new version.</p>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <input
            type="checkbox"
            id={`${testBase}-required`}
            checked={question.required === true}
            onChange={(e) => onChange({ ...question, required: e.target.checked })}
            className="h-3.5 w-3.5"
            data-testid={`checkbox-${testBase}-required`}
          />
          <Label htmlFor={`${testBase}-required`} className="text-xs cursor-pointer">Required</Label>
        </div>
      </div>

      {isAck && (
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Acknowledgment Label (optional)</Label>
          <Input
            value={(question.acknowledgmentLabel as string) ?? ""}
            placeholder="Checkbox label shown to the participant…"
            onChange={(e) => onChange({ ...question, acknowledgmentLabel: e.target.value || undefined })}
            data-testid={`input-${testBase}-ack-label`}
            className="text-sm h-8"
          />
        </div>
      )}

      {/* Plain options list — only for select dropdown or choice types NOT in a scored section */}
      {hasOptions && !showAnswerKeyEditor && (
        <div className="space-y-1.5">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Options</Label>
          <V2ItemListEditor
            items={(question.options as string[]) ?? []}
            onChange={(next) => onChange({ ...question, options: next })}
            isLive={false}
            testBase={`${testBase}-options`}
            placeholder="Option text…"
          />
        </div>
      )}

      {/* Answer-key editor — for exam questions and scored-section choice questions */}
      {showAnswerKeyEditor && (
        <ExamQuestionOptionsEditor
          options={(question.options as string[]) ?? []}
          correctAnswerIndex={question.correctAnswerIndex as number | undefined}
          onChange={({ options, correctAnswerIndex }) =>
            onChange({ ...question, options, correctAnswerIndex })
          }
          testBase={testBase}
        />
      )}
    </div>
  );
}

// ─── V2 Expandable Editor ───────────────────────────────────────────────────

function V2ExpandableEditor({
  expandable,
  isLive,
  testBase,
  onChange,
}: {
  expandable: Record<string, unknown>;
  isLive: boolean;
  testBase: string;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const kind = (expandable.kind as string) ?? "regular";
  const body = Array.isArray(expandable.body) ? (expandable.body as Array<Record<string, unknown>>) : null;
  const definitions = Array.isArray(expandable.definitions)
    ? (expandable.definitions as Array<Record<string, unknown>>)
    : null;
  const steps = Array.isArray(expandable.steps)
    ? (expandable.steps as Array<Record<string, unknown>>)
    : null;

  const updateDef = (i: number, field: "term" | "description", val: string) => {
    const next = [...(definitions ?? [])];
    next[i] = { ...next[i], [field]: val };
    onChange({ ...expandable, definitions: next });
  };
  const addDef = () => onChange({ ...expandable, definitions: [...(definitions ?? []), { term: "", description: "" }] });
  const removeDef = (i: number) => onChange({ ...expandable, definitions: (definitions ?? []).filter((_, j) => j !== i) });

  const updateStep = (i: number, field: "number" | "text", val: string | number) => {
    const next = [...(steps ?? [])];
    next[i] = { ...next[i], [field]: val };
    onChange({ ...expandable, steps: next });
  };
  const addStep = () => {
    const maxN = (steps ?? []).reduce((m, s) => Math.max(m, (s.number as number) ?? 0), 0);
    onChange({ ...expandable, steps: [...(steps ?? []), { number: maxN + 1, text: "" }] });
  };
  const removeStep = (i: number) => onChange({ ...expandable, steps: (steps ?? []).filter((_, j) => j !== i) });

  return (
    <div className="space-y-2">
      {/* Kind selector — structural, disabled on live */}
      <div className="space-y-1">
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Kind</Label>
        <Select
          value={kind}
          onValueChange={(v) => onChange({ ...expandable, kind: v })}
          disabled={isLive}
        >
          <SelectTrigger className="w-44 h-8 text-xs" data-testid={`select-${testBase}-exp-kind`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="regular" className="text-xs">Regular</SelectItem>
            <SelectItem value="sample-response" className="text-xs">Sample Response</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Title</Label>
        <Input
          value={(expandable.title as string) ?? ""}
          placeholder="Expandable title…"
          onChange={(e) => onChange({ ...expandable, title: e.target.value })}
          data-testid={`input-${testBase}-exp-title`}
          className="text-sm h-8"
        />
      </div>

      {/* sample-response: sampleText field */}
      {kind === "sample-response" && (
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Sample Text</Label>
          <Textarea
            value={(expandable.sampleText as string) ?? ""}
            rows={4}
            placeholder="Sample response text (markdown supported)…"
            onChange={(e) => onChange({ ...expandable, sampleText: e.target.value })}
            data-testid={`input-${testBase}-exp-sampletext`}
            className="text-sm"
          />
        </div>
      )}

      {/* regular: all content fields */}
      {kind === "regular" && (
        <>
          {typeof expandable.intro === "string" && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Intro</Label>
              <Textarea
                value={expandable.intro as string}
                rows={3}
                onChange={(e) => onChange({ ...expandable, intro: e.target.value })}
                data-testid={`input-${testBase}-exp-intro`}
                className="text-sm"
              />
            </div>
          )}

          {/* Definitions */}
          {definitions !== null && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Definitions</Label>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={addDef}
                  data-testid={`button-add-def-${testBase}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              {definitions.map((def, i) => (
                <div key={i} className="flex gap-1 items-start">
                  <div className="flex-1 space-y-1">
                    <Input
                      value={(def.term as string) ?? ""}
                      placeholder="Term…"
                      onChange={(e) => updateDef(i, "term", e.target.value)}
                      className="text-xs h-7"
                      data-testid={`input-${testBase}-def-term-${i}`}
                    />
                    <Input
                      value={(def.description as string) ?? ""}
                      placeholder="Description…"
                      onChange={(e) => updateDef(i, "description", e.target.value)}
                      className="text-xs h-7"
                      data-testid={`input-${testBase}-def-desc-${i}`}
                    />
                  </div>
                  <Button size="icon" variant="ghost" className="h-6 w-6 mt-0.5" disabled={isLive}
                    onClick={() => removeDef(i)}
                    data-testid={`button-remove-def-${testBase}-${i}`}>
                    {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Steps */}
          {steps !== null && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Steps</Label>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={addStep}
                  data-testid={`button-add-step-${testBase}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              {steps.map((step, i) => (
                <div key={i} className="flex gap-1 items-center">
                  <Input
                    type="number"
                    value={(step.number as number) ?? i + 1}
                    onChange={(e) => updateStep(i, "number", parseInt(e.target.value, 10) || i + 1)}
                    className="text-xs h-7 w-14 shrink-0"
                    data-testid={`input-${testBase}-step-num-${i}`}
                  />
                  <Input
                    value={(step.text as string) ?? ""}
                    placeholder="Step text…"
                    onChange={(e) => updateStep(i, "text", e.target.value)}
                    className="text-xs h-7 flex-1"
                    data-testid={`input-${testBase}-step-text-${i}`}
                  />
                  <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" disabled={isLive}
                    onClick={() => removeStep(i)}
                    data-testid={`button-remove-step-${testBase}-${i}`}>
                    {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Examples */}
          {Array.isArray(expandable.examples) && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Examples</Label>
              <V2ItemListEditor
                items={(expandable.examples as string[]).filter((e) => typeof e === "string")}
                onChange={(next) => onChange({ ...expandable, examples: next })}
                isLive={isLive}
                testBase={`${testBase}-exp-examples`}
                placeholder="Example…"
              />
            </div>
          )}

          {Array.isArray(expandable.tips) && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Tips</Label>
              <V2ItemListEditor
                items={(expandable.tips as string[]).filter((t) => typeof t === "string")}
                onChange={(next) => onChange({ ...expandable, tips: next })}
                isLive={isLive}
                testBase={`${testBase}-exp-tips`}
                placeholder="Tip text…"
              />
            </div>
          )}

          {Array.isArray(expandable.phrases) && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Phrases</Label>
              <V2ItemListEditor
                items={(expandable.phrases as string[]).filter((p) => typeof p === "string")}
                onChange={(next) => onChange({ ...expandable, phrases: next })}
                isLive={isLive}
                testBase={`${testBase}-exp-phrases`}
                placeholder="Phrase…"
              />
            </div>
          )}

          {Array.isArray(expandable.doList) && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Do List</Label>
              <V2ItemListEditor
                items={(expandable.doList as string[]).filter((t) => typeof t === "string")}
                onChange={(next) => onChange({ ...expandable, doList: next })}
                isLive={isLive}
                testBase={`${testBase}-exp-do`}
                placeholder="Do item…"
              />
            </div>
          )}

          {Array.isArray(expandable.dontList) && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Don't List</Label>
              <V2ItemListEditor
                items={(expandable.dontList as string[]).filter((t) => typeof t === "string")}
                onChange={(next) => onChange({ ...expandable, dontList: next })}
                isLive={isLive}
                testBase={`${testBase}-exp-dont`}
                placeholder="Don't item…"
              />
            </div>
          )}

          {body !== null && (
            <V2BlockListEditor
              label="Body Blocks"
              blocks={body}
              sectionIdx={0}
              fieldKey={`${testBase}-exp-body`}
              isLive={isLive}
              onChange={(next) => onChange({ ...expandable, body: next })}
            />
          )}

          {typeof expandable.note === "string" && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Note</Label>
              <Textarea
                value={expandable.note as string}
                rows={2}
                onChange={(e) => onChange({ ...expandable, note: e.target.value })}
                data-testid={`input-${testBase}-exp-note`}
                className="text-sm"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── V2 Expandable List (for sections.expandables: V2Expandable[]) ──────────

function V2ExpandablesList({
  expandables,
  sectionIdx,
  isLive,
  onChange,
}: {
  expandables: Array<Record<string, unknown>>;
  sectionIdx: number;
  isLive: boolean;
  onChange: (next: Array<Record<string, unknown>>) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

  const updateOne = (i: number, next: Record<string, unknown>) => {
    const arr = [...expandables];
    arr[i] = next;
    onChange(arr);
  };

  const addExpandable = () => {
    onChange([...expandables, { kind: "regular", title: "New Expandable", body: [] }]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Expandable Panels ({expandables.length})</Label>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addExpandable}
          data-testid={`button-add-expandable-${sectionIdx}`}>
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {expandables.map((exp, i) => (
        <Card key={i} className="border-muted">
          <CardContent className="py-3 space-y-2.5">
            <div className="flex items-center gap-2">
              <button
                className="flex-1 text-left text-xs font-medium truncate"
                onClick={() => setCollapsed((c) => ({ ...c, [i]: !c[i] }))}
                data-testid={`button-toggle-expandable-${sectionIdx}-${i}`}
              >
                {collapsed[i] ? <ChevronDown className="w-3 h-3 inline mr-1" /> : <ChevronUp className="w-3 h-3 inline mr-1" />}
                {(exp.title as string) || `Expandable ${i + 1}`}
              </button>
              <Button size="icon" variant="ghost" className="h-6 w-6" disabled={isLive}
                onClick={() => onChange(expandables.filter((_, j) => j !== i))}
                data-testid={`button-remove-expandable-${sectionIdx}-${i}`}>
                {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
              </Button>
            </div>

            {!collapsed[i] && (
              <V2ExpandableEditor
                expandable={exp}
                isLive={isLive}
                testBase={`sec-${sectionIdx}-exp-${i}`}
                onChange={(next) => updateOne(i, next)}
              />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Quote Section Fields ───────────────────────────────────────────────────

function QuoteSectionFields({
  section,
  sectionIdx,
  onUpdate,
}: {
  section: Record<string, unknown>;
  sectionIdx: number;
  onUpdate: (m: (s: Record<string, unknown>) => void) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-primary/20 bg-primary/5 p-3">
      <p className="text-xs font-semibold text-primary uppercase tracking-wide">Quote Content</p>

      <div className="space-y-1.5">
        <Label className="text-xs">Quote Text</Label>
        <Textarea
          value={(section.text as string) ?? ""}
          rows={3}
          placeholder="The quote text…"
          onChange={(e) => onUpdate((s) => { s.text = e.target.value; })}
          data-testid={`input-section-${sectionIdx}-quote-text`}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Author</Label>
        <Input
          value={(section.author as string) ?? ""}
          placeholder="Author name…"
          onChange={(e) => onUpdate((s) => { s.author = e.target.value; })}
          data-testid={`input-section-${sectionIdx}-quote-author`}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Theme Line (optional)</Label>
        <Input
          value={(section.themeLine as string) ?? ""}
          placeholder="Shown below the author on top-position quotes…"
          onChange={(e) => onUpdate((s) => {
            if (e.target.value) s.themeLine = e.target.value;
            else delete s.themeLine;
          })}
          data-testid={`input-section-${sectionIdx}-quote-theme`}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Position</Label>
        <Select
          value={(section.position as string) ?? "bottom"}
          onValueChange={(v) => onUpdate((s) => { s.position = v; })}
        >
          <SelectTrigger className="h-8 text-xs w-36" data-testid={`select-section-${sectionIdx}-quote-position`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="top">Top (Today's Focus)</SelectItem>
            <SelectItem value="bottom">Bottom</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Welcome Section Step Editor ────────────────────────────────────────────

function WelcomeStepsEditor({
  steps,
  sectionIdx,
  isLive,
  onUpdate,
}: {
  steps: Array<Record<string, unknown>>;
  sectionIdx: number;
  isLive: boolean;
  onUpdate: (m: (s: Record<string, unknown>) => void) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

  const updateStep = (i: number, mutator: (step: Record<string, unknown>) => void) => {
    onUpdate((s) => {
      const arr = Array.isArray(s.steps) ? [...(s.steps as Array<Record<string, unknown>>)] : [];
      const copy = { ...arr[i] };
      mutator(copy);
      arr[i] = copy;
      s.steps = arr;
    });
  };

  const addStep = () => {
    onUpdate((s) => {
      const arr = Array.isArray(s.steps) ? [...(s.steps as Array<Record<string, unknown>>)] : [];
      arr.push({ number: arr.length + 1, title: "New Step", body: [] });
      s.steps = arr;
    });
  };

  const removeStep = (i: number) => {
    onUpdate((s) => {
      const arr = Array.isArray(s.steps) ? (s.steps as Array<Record<string, unknown>>) : [];
      s.steps = arr.filter((_, j) => j !== i);
    });
  };

  return (
    <div className="space-y-2 rounded-md border border-brand-200 bg-brand-50/30 dark:bg-brand-950/10 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-brand-700 dark:text-brand-400 uppercase tracking-wide">
          Welcome Steps ({steps.length})
        </p>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addStep}
          data-testid={`button-add-step-${sectionIdx}`}>
          <Plus className="w-3 h-3 mr-1" /> Add Step
        </Button>
      </div>

      {steps.map((step, i) => (
        <Card key={i} className="border-muted">
          <CardContent className="py-3 space-y-2">
            <div className="flex items-center gap-2">
              <button
                className="flex-1 text-left text-xs font-medium truncate"
                onClick={() => setCollapsed((c) => ({ ...c, [i]: !c[i] }))}
                data-testid={`button-toggle-step-${sectionIdx}-${i}`}
              >
                {collapsed[i] ? <ChevronDown className="w-3 h-3 inline mr-1" /> : <ChevronUp className="w-3 h-3 inline mr-1" />}
                Step {(step.number as number) ?? i + 1}: {(step.title as string) || "(untitled)"}
              </button>
              <Button size="icon" variant="ghost" className="h-6 w-6" disabled={isLive}
                title={isLive ? "Removing steps requires a new version" : "Remove"}
                onClick={() => removeStep(i)} data-testid={`button-remove-step-${sectionIdx}-${i}`}>
                {isLive ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
              </Button>
            </div>

            {!collapsed[i] && (
              <div className="space-y-2 pt-1">
                <div className="flex gap-2">
                  <div className="space-y-1 w-20">
                    <Label className="text-[10px] text-muted-foreground">Number</Label>
                    <Input
                      type="number"
                      value={String(step.number ?? i + 1)}
                      onChange={(e) => updateStep(i, (st) => { st.number = Number(e.target.value); })}
                      className="h-8 text-xs"
                      data-testid={`input-step-${sectionIdx}-${i}-number`}
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Title</Label>
                    <Input
                      value={(step.title as string) ?? ""}
                      placeholder="Step title…"
                      onChange={(e) => updateStep(i, (st) => { st.title = e.target.value; })}
                      className="h-8 text-xs"
                      data-testid={`input-step-${sectionIdx}-${i}-title`}
                    />
                  </div>
                </div>

                {isV2BlockArray(step.body) && (
                  <V2BlockListEditor
                    label="Body Blocks"
                    blocks={step.body as Array<Record<string, unknown>>}
                    sectionIdx={sectionIdx * 100 + i}
                    fieldKey={`step-body`}
                    isLive={isLive}
                    onChange={(next) => updateStep(i, (st) => { st.body = next; })}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Enhanced Question List ─────────────────────────────────────────────────
/**
 * Edits questions in-place. Now supports:
 * - Prompt text editing
 * - Question type selector (structural change on live programs)
 * - Options list for choice-based types
 * - acknowledgmentLabel for acknowledgment type
 */
function QuestionList({
  questions,
  isLive,
  onChange,
  sectionIdx,
  inScoredSection,
}: {
  questions: Array<Record<string, unknown> | string>;
  isLive: boolean;
  onChange: (next: Array<Record<string, unknown> | string>) => void;
  sectionIdx?: number;
  /** When true, single-choice and multi-select questions show the correct-answer
   * radio selector so admins can designate the right answer for weekly quizzes. */
  inScoredSection?: boolean;
}) {
  const textKeyOf = (q: Record<string, unknown> | string): "prompt" | "label" => {
    if (typeof q === "string") return "prompt";
    if (typeof q.label === "string" && typeof q.prompt !== "string") return "label";
    return "prompt";
  };

  const promptOf = (q: Record<string, unknown> | string): string => {
    if (typeof q === "string") return q;
    const key = textKeyOf(q);
    const v = q[key];
    return typeof v === "string" ? v : "";
  };

  const isV2Question = (q: Record<string, unknown> | string): boolean => {
    if (typeof q === "string") return false;
    return typeof q.type === "string" && V2_FIELD_TYPES.some((ft) => ft.value === q.type);
  };

  const addQuestion = () => onChange([...questions, { prompt: "", type: "short-text", required: true }]);
  const removeQuestion = (i: number) => onChange(questions.filter((_, j) => j !== i));

  const updateQuestion = (i: number, mutator: (q: Record<string, unknown>) => void) => {
    const next = [...questions];
    const cur = next[i];
    const obj: Record<string, unknown> = typeof cur === "string" ? { prompt: cur } : { ...cur };
    mutator(obj);
    next[i] = obj;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Questions ({questions.length})</Label>
        <Button size="sm" variant="ghost" onClick={addQuestion} data-testid="button-add-questions">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
      {questions.map((q, i) => {
        const value = promptOf(q);
        const isObj = typeof q === "object" && q !== null;
        const hasV2Type = isObj && isV2Question(q);
        const qType = isObj ? ((q as Record<string, unknown>).type as string) : undefined;
        const hasOptions = qType === "single-choice" || qType === "multi-select" || qType === "select";
        const isExamQuestion = qType === "exam-question";
        const isAck = qType === "acknowledgment";
        const options = isObj && Array.isArray((q as Record<string, unknown>).options)
          ? ((q as Record<string, unknown>).options as string[])
          : null;
        const correctAnswerIndex = isObj ? (q as Record<string, unknown>).correctAnswerIndex as number | undefined : undefined;
        const ackLabel = isObj ? (q as Record<string, unknown>).acknowledgmentLabel as string | undefined : undefined;
        const required = isObj ? (q as Record<string, unknown>).required : undefined;

        const multiline = value.length > 80 || value.includes("\n");
        const testPrefix = sectionIdx !== undefined ? `q-${sectionIdx}-${i}` : `questions-${i}`;

        return (
          <div key={i} className="rounded-md border bg-muted/20 p-2.5 space-y-2">
            <div className="flex items-start gap-2">
              {multiline ? (
                <Textarea
                  value={value}
                  rows={Math.min(8, Math.max(3, Math.ceil(value.length / 80)))}
                  placeholder="Question prompt…"
                  onChange={(e) => updateQuestion(i, (obj) => {
                    const key = textKeyOf(q);
                    obj[key] = e.target.value;
                  })}
                  data-testid={`input-${testPrefix}-prompt`}
                  className="text-sm"
                />
              ) : (
                <Input
                  value={value}
                  placeholder="Question prompt…"
                  onChange={(e) => updateQuestion(i, (obj) => {
                    const key = textKeyOf(q);
                    obj[key] = e.target.value;
                  })}
                  data-testid={`input-${testPrefix}-prompt`}
                  className="text-sm"
                />
              )}
              <Button
                size="icon"
                variant="ghost"
                className="shrink-0"
                disabled={isLive}
                title={isLive ? "Removing questions requires a new version" : "Remove"}
                onClick={() => removeQuestion(i)}
                data-testid={`button-remove-${testPrefix}`}
              >
                {isLive ? <Lock className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
              </Button>
            </div>

            {hasV2Type && (
              <div className="flex gap-2 items-end flex-wrap">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Type</Label>
                  <Select
                    value={qType ?? "short-text"}
                    onValueChange={(v) => updateQuestion(i, (obj) => {
                      obj.type = v;
                      if (!["single-choice", "multi-select", "select", "exam-question"].includes(v)) delete obj.options;
                      if (v === "exam-question" && !Array.isArray(obj.options)) {
                        obj.options = [];
                        delete obj.correctAnswerIndex;
                      }
                      // In scored sections, initialize correctAnswerIndex for single-choice
                      // (multi-select excluded — incompatible JSON-array storage format)
                      if (inScoredSection && v === "single-choice") {
                        if (obj.correctAnswerIndex === undefined) obj.correctAnswerIndex = 0;
                        if (!Array.isArray(obj.options)) obj.options = [];
                      }
                      // Only clear correctAnswerIndex when leaving all answer-key-eligible types
                      const keepCorrectAnswer =
                        v === "exam-question" ||
                        (inScoredSection && v === "single-choice");
                      if (!keepCorrectAnswer) delete obj.correctAnswerIndex;
                      if (v !== "acknowledgment") delete obj.acknowledgmentLabel;
                    })}
                    disabled={isLive}
                  >
                    <SelectTrigger className="w-40 h-7 text-xs" data-testid={`select-${testPrefix}-type`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {V2_FIELD_TYPES.map((ft) => (
                        <SelectItem key={ft.value} value={ft.value} className="text-xs">{ft.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isLive && (
                    <p className="text-[10px] text-amber-600">Requires new version on live programs.</p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 mb-1">
                  <input
                    type="checkbox"
                    id={`${testPrefix}-required`}
                    checked={required === true}
                    onChange={(e) => updateQuestion(i, (obj) => { obj.required = e.target.checked; })}
                    className="h-3.5 w-3.5"
                    data-testid={`checkbox-${testPrefix}-required`}
                  />
                  <Label htmlFor={`${testPrefix}-required`} className="text-xs cursor-pointer">Required</Label>
                </div>
              </div>
            )}

            {isAck && (
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Acknowledgment Label</Label>
                <Input
                  value={ackLabel ?? ""}
                  placeholder="Checkbox label (leave blank to use prompt)…"
                  onChange={(e) => updateQuestion(i, (obj) => {
                    if (e.target.value) obj.acknowledgmentLabel = e.target.value;
                    else delete obj.acknowledgmentLabel;
                  })}
                  data-testid={`input-${testPrefix}-ack-label`}
                  className="text-sm h-8"
                />
              </div>
            )}

            {hasOptions && !isExamQuestion && !(inScoredSection && qType === "single-choice") && (
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Options</Label>
                <V2ItemListEditor
                  items={options ?? []}
                  onChange={(next) => updateQuestion(i, (obj) => { obj.options = next; })}
                  isLive={false}
                  testBase={`${testPrefix}-opts`}
                  placeholder="Option text…"
                />
              </div>
            )}

            {(isExamQuestion || (inScoredSection && qType === "single-choice")) && (
              <ExamQuestionOptionsEditor
                options={options ?? []}
                correctAnswerIndex={correctAnswerIndex}
                onChange={({ options: nextOpts, correctAnswerIndex: nextIdx }) =>
                  updateQuestion(i, (obj) => {
                    obj.options = nextOpts;
                    obj.correctAnswerIndex = nextIdx;
                  })
                }
                testBase={testPrefix}
              />
            )}

            {!hasV2Type && isObj && Object.keys(q as Record<string, unknown>).filter((k) => k !== textKeyOf(q)).length > 0 && (
              <p className="text-[10px] text-muted-foreground pl-1">
                Preserved metadata: {Object.keys(q as Record<string, unknown>).filter((k) => k !== textKeyOf(q)).join(", ")}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BulletList({
  label,
  items,
  onChange,
  isLive,
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
  isLive: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onChange([...items, ""])}
          data-testid={`button-add-${label.toLowerCase()}`}
        >
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={item}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            data-testid={`input-${label.toLowerCase()}-${i}`}
          />
          <Button
            size="icon"
            variant="ghost"
            disabled={isLive}
            title={isLive ? "Removing items requires a new version" : "Remove"}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            data-testid={`button-remove-${label.toLowerCase()}-${i}`}
          >
            {isLive ? <Lock className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
          </Button>
        </div>
      ))}
    </div>
  );
}

function SectionEditor({
  section,
  idx,
  sectionsCount,
  isLive,
  currentDayNumber,
  allDays,
  onUpdate,
  onRemove,
  onMove,
}: {
  section: Record<string, unknown>;
  idx: number;
  sectionsCount: number;
  isLive: boolean;
  currentDayNumber: number;
  allDays: ProgramDay[];
  onUpdate: (m: (s: Record<string, unknown>) => void) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const bookmarks = readBookmarks(section);
  const title = (section.title as string) || `Section ${idx + 1}`;

  // Detect V2 section type
  const sectionType = typeof section.type === "string" ? section.type : null;
  const isV2 = sectionType !== null && V2_SECTION_TYPES.has(sectionType);
  const isQuote = sectionType === "quote";
  const isWelcome = sectionType === "welcome";
  const isScored = sectionType === "scored";

  // Detect V2 body blocks
  const bodyBlocks = isV2BlockArray(section.body) ? (section.body as Array<Record<string, unknown>>) : null;

  // Detect V2 ordered content — for V2 sections, an empty array is still ordered content
  const orderedContent = (isV2 && Array.isArray(section.content))
    ? (section.content as Array<Record<string, unknown>>)
    : isV2OrderedContent(section.content)
    ? (section.content as Array<Record<string, unknown>>)
    : null;

  // V2 expandables — for V2 sections, empty array is still a V2 expandables list
  const v2Expandables = (isV2 && Array.isArray(section.expandables))
    ? (section.expandables as Array<Record<string, unknown>>)
    : (Array.isArray(section.expandables) &&
        (section.expandables as Array<Record<string, unknown>>).length > 0 &&
        typeof (section.expandables as Array<Record<string, unknown>>)[0]?.kind === "string")
    ? (section.expandables as Array<Record<string, unknown>>)
    : null;

  // Old-format expandables (have content.intro / content.tips shape) — only for non-V2 sections
  const legacyExpandables = !isV2 &&
    Array.isArray(section.expandables) &&
    (section.expandables as unknown[]).length > 0
    ? (section.expandables as Array<Record<string, unknown>>)
    : null;

  // Welcome steps
  const welcomeSteps = isWelcome && Array.isArray(section.steps)
    ? (section.steps as Array<Record<string, unknown>>)
    : null;

  return (
    <Card data-testid={`card-section-${idx}`}>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button
            className="flex-1 text-left flex items-center gap-2"
            onClick={() => setCollapsed((c) => !c)}
            data-testid={`button-toggle-section-${idx}`}
          >
            {collapsed ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
            <span className="font-medium text-sm">
              {idx + 1}. {title}
            </span>
            {sectionType && (
              <Badge variant="outline" className="text-[10px]">{sectionType}</Badge>
            )}
            {bookmarks.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                <BookmarkIcon className="w-3 h-3 mr-1" />
                {bookmarks.length}
              </Badge>
            )}
          </button>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              disabled={isLive || idx === 0}
              title={isLive ? "Reordering requires a new version" : "Move up"}
              onClick={() => onMove(-1)}
              data-testid={`button-move-up-section-${idx}`}
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              disabled={isLive || idx === sectionsCount - 1}
              title={isLive ? "Reordering requires a new version" : "Move down"}
              onClick={() => onMove(1)}
              data-testid={`button-move-down-section-${idx}`}
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              disabled={isLive}
              title={isLive ? "Removing requires a new version" : "Remove"}
              onClick={onRemove}
              data-testid={`button-remove-section-${idx}`}
            >
              {isLive ? (
                <Lock className="w-3.5 h-3.5" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>
        </div>

        {!collapsed && (
          <div className="space-y-3 pt-2 border-t">
            {/* ── Quote-specific fields ─────────────────────────── */}
            {isQuote && (
              <QuoteSectionFields
                section={section}
                sectionIdx={idx}
                onUpdate={onUpdate}
              />
            )}

            {/* ── Generic safe string fields (skip for quote — they use dedicated UI) */}
            {!isQuote && SAFE_SECTION_FIELDS.map((field) => {
              const v = section[field.key];
              // Hide if not a string (e.g. body: V2Block[] hides the old "content" textarea)
              if (typeof v !== "string" && v !== undefined) return null;
              if (v === undefined && !["title", "content", "prompt"].includes(field.key)) return null;
              // Hide content field if we're showing ordered-content editor
              if (field.key === "content" && orderedContent) return null;
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label className="text-xs">{field.label}</Label>
                  {field.multiline ? (
                    <Textarea
                      value={(v as string) ?? ""}
                      rows={3}
                      onChange={(e) =>
                        onUpdate((s) => {
                          s[field.key] = e.target.value;
                        })
                      }
                      data-testid={`input-section-${idx}-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={(v as string) ?? ""}
                      onChange={(e) =>
                        onUpdate((s) => {
                          s[field.key] = e.target.value;
                        })
                      }
                      data-testid={`input-section-${idx}-${field.key}`}
                    />
                  )}
                </div>
              );
            })}

            {/* ── Points field ──────────────────────────────────── */}
            {typeof section.points === "number" && (
              <div className="space-y-1.5 max-w-[160px]">
                <Label className="text-xs">Points</Label>
                <Input
                  type="number"
                  value={String(section.points)}
                  onChange={(e) =>
                    onUpdate((s) => {
                      const n = Number(e.target.value);
                      s.points = Number.isFinite(n) ? n : 0;
                    })
                  }
                  data-testid={`input-section-${idx}-points`}
                />
              </div>
            )}

            {/* ── Scored section flags ──────────────────────────── */}
            {(isScored || section.isReflectionSection === true) && (
              <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Section Flags</p>

                <div className="flex items-start gap-2">
                  <input
                    id={`section-${idx}-save-only`}
                    type="checkbox"
                    checked={section.saveOnly === true}
                    onChange={(e) =>
                      onUpdate((s) => {
                        if (e.target.checked) s.saveOnly = true;
                        else delete s.saveOnly;
                      })
                    }
                    className="h-4 w-4 rounded border-input mt-0.5"
                    data-testid={`checkbox-section-${idx}-save-only`}
                  />
                  <div>
                    <Label htmlFor={`section-${idx}-save-only`} className="text-xs cursor-pointer font-medium">
                      Save-only section
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Hides question inputs — participants just read the content and click Save to earn points.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    id={`section-${idx}-is-reflection`}
                    type="checkbox"
                    checked={section.isReflectionSection === true}
                    onChange={(e) =>
                      onUpdate((s) => {
                        if (e.target.checked) {
                          s.isReflectionSection = true;
                        } else {
                          delete s.isReflectionSection;
                        }
                      })
                    }
                    className="h-4 w-4 rounded border-input mt-0.5"
                    data-testid={`checkbox-section-${idx}-is-reflection`}
                  />
                  <div>
                    <Label htmlFor={`section-${idx}-is-reflection`} className="text-xs cursor-pointer font-medium">
                      Daily reflection section
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Awards 25-pt on-time bonus and saves answers as a journal entry.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── V2 Ordered content (interleaved blocks/questions/expandables) */}
            {orderedContent && (
              <V2ContentEditor
                content={orderedContent}
                sectionIdx={idx}
                isLive={isLive}
                onChange={(next) => onUpdate((s) => { s.content = next; })}
                inScoredSection={isScored}
              />
            )}

            {/* ── V2 Body blocks (when no ordered content) ─────── */}
            {bodyBlocks !== null && !orderedContent && (
              <V2BlockListEditor
                label="Body Blocks"
                blocks={bodyBlocks}
                sectionIdx={idx}
                fieldKey="body"
                isLive={isLive}
                onChange={(next) => onUpdate((s) => { s.body = next; })}
              />
            )}

            {/* ── Welcome section steps ─────────────────────────── */}
            {isWelcome && welcomeSteps !== null && (
              <WelcomeStepsEditor
                steps={welcomeSteps}
                sectionIdx={idx}
                isLive={isLive}
                onUpdate={onUpdate}
              />
            )}

            {/* ── V2 Expandables (V2Expandable[] with kind field) ─ */}
            {v2Expandables && !orderedContent && (
              <V2ExpandablesList
                expandables={v2Expandables}
                sectionIdx={idx}
                isLive={isLive}
                onChange={(next) => onUpdate((s) => { s.expandables = next; })}
              />
            )}

            {/* ── Questions ─────────────────────────────────────── */}
            {Array.isArray(section.questions) && !orderedContent && (
              <QuestionList
                questions={section.questions as Array<Record<string, unknown> | string>}
                isLive={isLive}
                sectionIdx={idx}
                inScoredSection={isScored}
                onChange={(next) =>
                  onUpdate((s) => {
                    s.questions = next;
                  })
                }
              />
            )}

            {/* ── Sample Response ───────────────────────────────── */}
            {typeof section.sampleResponse === "string" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Sample Response</Label>
                <Textarea
                  value={section.sampleResponse as string}
                  rows={4}
                  onChange={(e) =>
                    onUpdate((s) => {
                      s.sampleResponse = e.target.value;
                    })
                  }
                  data-testid={`input-section-${idx}-sample-response`}
                />
              </div>
            )}

            {/* ── Legacy expandables (old content.{intro,tips} shape) */}
            {legacyExpandables && (
              <ExpandablesList
                expandables={legacyExpandables}
                sectionIdx={idx}
                isLive={isLive}
                onChange={(next) =>
                  onUpdate((s) => {
                    s.expandables = next;
                  })
                }
              />
            )}

            {/* ── Bookmark manager ──────────────────────────────── */}
            <BookmarkManager
              bookmarks={bookmarks}
              currentDayNumber={currentDayNumber}
              allDays={allDays}
              onChange={(next) =>
                onUpdate((s) => {
                  s.journalReferences = next;
                  delete s.journalReference;
                })
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Legacy Expandables List (old content.{intro,tips} shape) ──────────────

function ExpandablesList({
  expandables,
  sectionIdx,
  isLive,
  onChange,
}: {
  expandables: Array<Record<string, unknown>>;
  sectionIdx: number;
  isLive: boolean;
  onChange: (next: Array<Record<string, unknown>>) => void;
}) {
  const updateOne = (i: number, mutator: (e: Record<string, unknown>) => void) => {
    const next = expandables.map((e) => ({ ...e }));
    const copy: Record<string, unknown> = JSON.parse(JSON.stringify(next[i] ?? {}));
    mutator(copy);
    next[i] = copy;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">Expandables ({expandables.length})</Label>
      {expandables.map((exp, i) => {
        const content = (typeof exp.content === "object" && exp.content !== null
          ? (exp.content as Record<string, unknown>)
          : {});
        const tips = Array.isArray(content.tips) ? (content.tips as unknown[]).filter((t) => typeof t === "string") as string[] : null;
        const levels = Array.isArray(content.levels) ? (content.levels as Array<Record<string, unknown>>) : null;
        const otherKeys = Object.keys(content).filter(
          (k) => !["intro", "tips", "tipsTitle", "levels", "levelsTitle"].includes(k),
        );
        return (
          <Card key={i} className="border-muted">
            <CardContent className="py-3 space-y-2.5">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Expandable {i + 1} — Title
                </Label>
                <Input
                  value={(exp.title as string) ?? ""}
                  onChange={(e) =>
                    updateOne(i, (d) => {
                      d.title = e.target.value;
                    })
                  }
                  data-testid={`input-expandable-${sectionIdx}-${i}-title`}
                />
              </div>

              {typeof content.intro === "string" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Intro</Label>
                  <Textarea
                    value={content.intro as string}
                    rows={4}
                    onChange={(e) =>
                      updateOne(i, (d) => {
                        const c = (typeof d.content === "object" && d.content !== null
                          ? (d.content as Record<string, unknown>)
                          : {});
                        c.intro = e.target.value;
                        d.content = c;
                      })
                    }
                    data-testid={`input-expandable-${sectionIdx}-${i}-intro`}
                  />
                </div>
              )}

              {typeof content.tipsTitle === "string" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Tips Title</Label>
                  <Input
                    value={content.tipsTitle as string}
                    onChange={(e) =>
                      updateOne(i, (d) => {
                        const c = d.content as Record<string, unknown>;
                        c.tipsTitle = e.target.value;
                      })
                    }
                    data-testid={`input-expandable-${sectionIdx}-${i}-tips-title`}
                  />
                </div>
              )}

              {tips && (
                <BulletList
                  label="Tips"
                  items={tips}
                  isLive={isLive}
                  onChange={(next) =>
                    updateOne(i, (d) => {
                      const c = d.content as Record<string, unknown>;
                      c.tips = next;
                    })
                  }
                />
              )}

              {typeof content.levelsTitle === "string" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Levels Title</Label>
                  <Input
                    value={content.levelsTitle as string}
                    onChange={(e) =>
                      updateOne(i, (d) => {
                        const c = d.content as Record<string, unknown>;
                        c.levelsTitle = e.target.value;
                      })
                    }
                    data-testid={`input-expandable-${sectionIdx}-${i}-levels-title`}
                  />
                </div>
              )}

              {levels && levels.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs">Levels</Label>
                  {levels.map((lv, li) => (
                    <div key={li} className="rounded-md border p-2 space-y-1.5">
                      <Input
                        value={(lv.name as string) ?? ""}
                        placeholder="Name"
                        onChange={(e) =>
                          updateOne(i, (d) => {
                            const c = d.content as Record<string, unknown>;
                            const nextLevels = [...(c.levels as Array<Record<string, unknown>>)];
                            nextLevels[li] = { ...nextLevels[li], name: e.target.value };
                            c.levels = nextLevels;
                          })
                        }
                        data-testid={`input-expandable-${sectionIdx}-${i}-level-${li}-name`}
                      />
                      <Textarea
                        value={(lv.description as string) ?? ""}
                        placeholder="Description"
                        rows={2}
                        onChange={(e) =>
                          updateOne(i, (d) => {
                            const c = d.content as Record<string, unknown>;
                            const nextLevels = [...(c.levels as Array<Record<string, unknown>>)];
                            nextLevels[li] = { ...nextLevels[li], description: e.target.value };
                            c.levels = nextLevels;
                          })
                        }
                        data-testid={`input-expandable-${sectionIdx}-${i}-level-${li}-description`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {otherKeys.length > 0 && (
                <p className="text-[10px] text-muted-foreground">
                  Preserved metadata: {otherKeys.join(", ")}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function BookmarkManager({
  bookmarks,
  currentDayNumber,
  allDays,
  onChange,
}: {
  bookmarks: JournalReference[];
  currentDayNumber: number;
  allDays: ProgramDay[];
  onChange: (next: JournalReference[]) => void;
}) {
  const eligibleDays = allDays.filter((d) => d.dayNumber !== currentDayNumber);

  const sectionTitleAt = (dayNumber: number, sectionIndex: number): string | undefined => {
    const d = allDays.find((x) => x.dayNumber === dayNumber);
    if (!d) return undefined;
    const parsed = parseSafe(d.content || "{}");
    const key = detectSectionKey(parsed);
    const sec = readSections(parsed, key)[sectionIndex];
    if (sec && typeof (sec as Record<string, unknown>).title === "string") {
      return (sec as Record<string, unknown>).title as string;
    }
    return undefined;
  };

  const updateOne = (i: number, patch: Partial<JournalReference>) => {
    const next = [...bookmarks];
    const merged: JournalReference = { ...next[i], ...patch };
    const inferredTitle = sectionTitleAt(merged.dayNumber, merged.sectionIndex);
    if (inferredTitle) merged.sectionTitle = inferredTitle;
    const prior = next[i];
    const priorAuto = !prior?.label || prior.label === "Reference" || prior.label === prior.sectionTitle;
    if (priorAuto && inferredTitle) merged.label = inferredTitle;
    next[i] = merged;
    onChange(next);
  };

  const addBookmark = () => {
    const first = eligibleDays[0];
    const dayNumber = first?.dayNumber ?? 1;
    const sectionIndex = 0;
    const sectionTitle = sectionTitleAt(dayNumber, sectionIndex);
    onChange([
      ...bookmarks,
      {
        dayNumber,
        sectionIndex,
        label: sectionTitle || "Reference",
        sectionTitle,
      },
    ]);
  };

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <BookmarkIcon className="w-3.5 h-3.5" />
          Journal Bookmarks ({bookmarks.length})
        </Label>
        <Button
          size="sm"
          variant="ghost"
          onClick={addBookmark}
          data-testid="button-add-bookmark"
        >
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {bookmarks.map((bm, i) => {
        const targetDay = allDays.find((d) => d.dayNumber === bm.dayNumber);
        const targetSections = targetDay
          ? readSections(parseSafe(targetDay.content || "{}"))
          : [];
        const dangling = !targetDay || bm.sectionIndex >= targetSections.length;
        return (
          <div
            key={i}
            className={`grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end p-2 rounded ${
              dangling ? "bg-destructive/10 border border-destructive/30" : "bg-background"
            }`}
            data-testid={`bookmark-row-${i}`}
          >
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Target Day</Label>
              <Select
                value={String(bm.dayNumber)}
                onValueChange={(v) =>
                  updateOne(i, { dayNumber: parseInt(v, 10), sectionIndex: 0 })
                }
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`select-bookmark-day-${i}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {eligibleDays.map((d) => (
                    <SelectItem key={d.id} value={String(d.dayNumber)}>
                      Day {d.dayNumber}: {d.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Section</Label>
              <Select
                value={String(bm.sectionIndex)}
                onValueChange={(v) => updateOne(i, { sectionIndex: parseInt(v, 10) })}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`select-bookmark-section-${i}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {targetSections.length === 0 ? (
                    <SelectItem value="0" disabled>
                      No sections
                    </SelectItem>
                  ) : (
                    targetSections.map((s, si) => (
                      <SelectItem key={si} value={String(si)}>
                        {(s as Record<string, unknown>).title as string || `Section ${si + 1}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Label</Label>
              <Input
                value={bm.label || ""}
                onChange={(e) => updateOne(i, { label: e.target.value })}
                className="h-8 text-xs"
                data-testid={`input-bookmark-label-${i}`}
              />
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => onChange(bookmarks.filter((_, j) => j !== i))}
              data-testid={`button-remove-bookmark-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            {dangling && (
              <p className="col-span-4 text-xs text-destructive flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Bookmark target doesn't exist.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
